import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { SessionService } from '../state/session/session.service';
import { SessionQuery } from '../state/session';
import { environment } from '../../../environments/environment';
import { GlobalService } from '../state/global';
import { catchError } from 'rxjs/operators';
import { AppComponent } from '../../app.component';
import { isTrustedApiRequestUrl } from './trusted-api-request-url';

@Injectable()
export class RequestInterceptor implements HttpInterceptor {
	readonly UNSECURED_ROUTES = [
		'cms',
		'/auth/basic/generate-sign-in-code',
		'/auth/basic/code-sign-in',
		's3.amazonaws.com',
		environment.cmsUrl,
		environment.taskerEngineUrl
	].filter((r: string) => !!r);

	// A 401 from these user-owned ad-platform data endpoints means the platform's
	// own OAuth token (DV360/CM360/Facebook) couldn't be refreshed — e.g. a stale
	// token left over after the Google client was rotated. The CCE app session is
	// still valid, so we must NOT log the user out. The export-destination pane
	// catches the error and keeps its Disconnect/reconnect control visible instead.
	// A genuine CCE session expiry still triggers logout via any other API request.
	readonly OAUTH_PLATFORM_DATA_ROUTES = ['/integrations/dv360/', '/integrations/cm360/', '/integrations/facebook/'];

	// Same platform-token semantics for the destination-connection data routes:
	// `GET /export-action/:id/{accounts,profiles}` and the admin curation routes
	// `GET /export-action/:id/connection/accounts[/validate]` answer 401
	// (DestinationDisconnectedError) when the DESTINATION's admin-owned OAuth
	// connection is dead — the CCE session is fine, so logging out would be
	// wrong. Matched with precise regexes (NOT a '/export-action/' substring):
	// a genuine session expiry on any other export-action call must still log
	// the user out.
	readonly OAUTH_PLATFORM_DATA_ROUTE_PATTERNS = [
		/\/export-action\/[^/]+\/(accounts|profiles)(\?|$)/,
		/\/export-action\/[^/]+\/connection\/accounts(\/validate)?(\?|$)/
	];

	constructor(
		private readonly sessionQuery: SessionQuery,
		private readonly globalService: GlobalService,
		readonly sessionService: SessionService,
		readonly router: Router
	) {}

	intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		const isTrustedApiRequest = this.isTrustedApiRequest(request.url);
		if (this.isUnsecuredRoute(request.url) || !isTrustedApiRequest) {
			return next.handle(request);
		}

		return next.handle(this.setAccessToken(request, this.sessionQuery.getToken())).pipe(
			catchError((err: HttpErrorResponse) => {
				if (isTrustedApiRequest) {
					// handle for errors only on API requests
					switch (err.status) {
						case 401:
							// Platform-token failure (DV360/CM360/Facebook) — keep the CCE
							// session; let the export pane prompt a reconnect/disconnect.
							if (this.isOAuthPlatformDataRoute(request.url)) {
								return throwError(() => err);
							}
							console.log('🔄 Handling expired session...');
							// Widget routes are unauthenticated — never redirect to login
							if (!window.location.pathname.startsWith('/widget/')) {
								this.sessionService.logout();
								const queryParams =
									window.location.pathname === '/'
										? undefined
										: { [AppComponent.LOGIN_REDIRECT_QUERY_PARAMETER]: window.location.pathname };
								this.router.navigate(['/login'], {
									queryParams,
									replaceUrl: true
								});
							}

							return throwError(() => ({
								...err,
								error: {
									...err.error,
									message: $localize`:Session expired error message@@authSessionExpiredMessage:Session expired, please sign-in.` // Friendly message
								}
							}));
					}
				}
				return throwError(() => err);
			})
		);
	}

	private setAccessToken(request: any, accessToken: string | undefined) {
		// We clone the request, because the original request is immutable
		return request.clone({
			setHeaders: {
				Authorization: `Bearer ${accessToken}`,
				'Accept-Language': this.globalService.getLocale()
			}
		});
	}

	private isUnsecuredRoute(requestUrl: string): boolean {
		let match = false;
		this.UNSECURED_ROUTES.forEach(part => {
			if (requestUrl.indexOf(part) > -1) {
				match = true;
			}
		});
		return match;
	}

	private isTrustedApiRequest(requestUrl: string): boolean {
		return isTrustedApiRequestUrl(requestUrl, environment.apiUrl, document.baseURI);
	}

	private isOAuthPlatformDataRoute(requestUrl: string): boolean {
		return (
			this.OAUTH_PLATFORM_DATA_ROUTES.some(part => requestUrl.indexOf(part) > -1) ||
			this.OAUTH_PLATFORM_DATA_ROUTE_PATTERNS.some(pattern => pattern.test(requestUrl))
		);
	}
}
